import React from 'react'
import { Checkbox, FormControlLabelProps, RadioGroupProps } from '@mui/material'
import _ from 'es-toolkit/compat'

import {
  CustomerFeatures,
  ExportDownloadFilenameConvention,
  FormValues,
  PreTestFeatures,
  SkywardenFusionStrategy,
  SkywardenSearchMode,
} from '../main-types'

// Type-safe keys for feature flags
type FeatureFlagKey =
  | keyof CustomerFeatures
  | 'has_option_rationale'
  | keyof PreTestFeatures
  | ExportDownloadFilenameConvention
  | SkywardenSearchMode
  | SkywardenFusionStrategy

// Centralized mapping of feature flag field names to human-readable labels
export const featureFlagLabels: Record<FeatureFlagKey, string> = {
  // Flags
  learn_v2: 'Learn by customer',
  has_option_rationale: 'Option rationales',
  possible_keys: 'Possible Keys',
  rationales: 'Item Rationales',
  probuilder_integration: 'ProBuilder Integration',
  item_types_v2: 'Item Types V2',
  model_type_filter: 'Model Type Filter',
  webhook_integration: 'Webhook Integration',
  language_selector: 'Language Selector',

  // Pre-test
  pretest_key: 'Key',
  pretest_content: 'Content',
  pretest_style: 'Style',
  pretest_bias: 'Bias',

  // Export
  export_file: 'Export Formats',
  use_identifier_for_exports: 'Use Item Identifier in Exports',
  images_zip_file_export: 'Download a zip file',
  export_math: 'Export Math Format',
  export_math_delimiter: 'Export Math Delimiter',
  export_download_filename_convention: 'Export Download Filename',

  // Experimental
  generate_math: 'Math Fixer',

  // Skywarden search options
  skywarden_search_mode: 'Search Mode',
  skywarden_fusion_strategy: 'Fusion Strategy',
  skywarden_fusion_weight: 'Fusion Weight',
  vector: 'Vector',
  hybrid: 'Hybrid',
  text: 'Text',
  rrf: 'RRF',
  weighted: 'Weighted',
  boost: 'Boost',

  // Filename convention values
  content_first: 'Content First',
  date_first: 'Date First',
  simple_descriptive: 'Simple Descriptive',
  concatenated_names: 'Concatenated Names',
}

export const getFeatureFlagLabel = (field: string): string => {
  return featureFlagLabels[field as FeatureFlagKey] ?? field
}

type UseFeatureFlagsPropsOptions = {
  values: FormValues
  disabled?: boolean
  onChange: (name: string, value: boolean | string | number | undefined) => void
}

export const filenameConventionOptions: {
  value: ExportDownloadFilenameConvention | ''
  label: string
  example: string
}[] = [
  {
    value: '',
    label: 'None',
    example: 'Generate_items.zip',
  },
  {
    value: 'content_first',
    label: featureFlagLabels.content_first,
    example: 'Math_Grade_5--3_items--2026-03-02.zip',
  },
  {
    value: 'date_first',
    label: featureFlagLabels.date_first,
    example: '2026-03-02--Math_Grade_5--7_items.zip',
  },
  {
    value: 'simple_descriptive',
    label: featureFlagLabels.simple_descriptive,
    example: 'Exported_items_2026-03-02_15_items.zip',
  },
  {
    value: 'concatenated_names',
    label: featureFlagLabels.concatenated_names,
    example: 'Unit_1_and_Unit_2.zip',
  },
]

export type FilenameConventionRadioGroupProps = Pick<
  RadioGroupProps,
  'value' | 'onChange' | 'sx'
> & {
  disabled?: boolean
}

export type SkywardenRadioGroupProps = Pick<RadioGroupProps, 'value' | 'onChange'> & {
  disabled?: boolean
}

export type SkywardenWeightInputProps = {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
}

export type SkywardenSectionProps = {
  searchModeProps: SkywardenRadioGroupProps
  fusionStrategyProps: SkywardenRadioGroupProps
  weightInputProps: SkywardenWeightInputProps
}

export const useFeatureFlagsProps = ({
  values,
  disabled,
  onChange,
}: UseFeatureFlagsPropsOptions) => {
  const getCheckboxProps = React.useCallback(
    (label: string, field: string): FormControlLabelProps => {
      let checked = false

      if (field === 'hasOptionRationale') {
        checked = values.hasOptionRationale
      } else if (field.includes('export_file.')) {
        const exportType = field.split('.').pop()!
        checked = values.features.export_file?.includes(exportType) ?? false
      } else if (field.startsWith('features.')) {
        const featureKey = field.replace('features.', '')
        checked = Boolean(_.get(values.features, featureKey, false))
      } else {
        checked = Boolean(_.get(values, field, false))
      }

      return {
        disabled,
        slotProps: { typography: { fontSize: 14 } },
        sx: { display: 'flex' },
        control: <Checkbox size="small" color="secondary" />,
        name: field,
        checked,
        label,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(field, e.target.checked),
      }
    },
    [values, disabled, onChange],
  )

  const getFilenameConventionProps = React.useCallback((): FilenameConventionRadioGroupProps => {
    return {
      value: values.features.export_download_filename_convention || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as ExportDownloadFilenameConvention | ''
        onChange('features.export_download_filename_convention', value) // '' for None
      },
      sx: { justifyContent: 'space-between', my: 1 },
      disabled,
    }
  }, [values.features.export_download_filename_convention, disabled, onChange])

  const getSkywardenSectionProps = React.useCallback((): SkywardenSectionProps => {
    return {
      searchModeProps: {
        value: values.features.skywarden_search_mode || 'vector',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange('features.skywarden_search_mode', e.target.value as SkywardenSearchMode)
        },
        disabled,
      },
      fusionStrategyProps: {
        value: values.features.skywarden_fusion_strategy || 'rrf',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange('features.skywarden_fusion_strategy', e.target.value as SkywardenFusionStrategy)
        },
        disabled,
      },
      weightInputProps: {
        value: values.features.skywarden_fusion_weight ?? 0.5,
        onChange: (value: number) => {
          onChange('features.skywarden_fusion_weight', value)
        },
        disabled,
      },
    }
  }, [
    values.features.skywarden_search_mode,
    values.features.skywarden_fusion_strategy,
    values.features.skywarden_fusion_weight,
    disabled,
    onChange,
  ])

  return { getCheckboxProps, getFilenameConventionProps, getSkywardenSectionProps }
}

export const handleFeatureFlagsChange = (
  values: FormValues,
  field: string,
  value: boolean | string | number | undefined,
): FormValues => {
  const result: FormValues = _.cloneDeep(values)

  if (field === 'hasOptionRationale') {
    result.hasOptionRationale = value as boolean
  } else if (field.startsWith('features.export_file.')) {
    const exportType = field.split('.').pop()!
    const currentExports = result.features.export_file ?? []
    result.features.export_file = value
      ? [...currentExports, exportType]
      : currentExports.filter((e) => e !== exportType)
  } else if (field === 'features.export_download_filename_convention') {
    result.features.export_download_filename_convention =
      (value as ExportDownloadFilenameConvention) || ''
  } else if (field.startsWith('features.')) {
    const featureKey = field.replace('features.', '')
    ;(result.features as Record<string, unknown>)[featureKey] = value
  }

  return result
}

export const defaultFeatureValues: FormValues['features'] = {
  export_file: [],
  export_math: '',
  export_math_delimiter: '',
  use_identifier_for_exports: false,
  images_zip_file_export: false,
  learn_v2: false,
  possible_keys: false,
  rationales: false,
  generate_math: false,
  probuilder_integration: false,
  item_types_v2: false,
  model_type_filter: false,
  webhook_integration: false,
  language_selector: false,
  pretest_bias: false,
  pretest_content: false,
  pretest_key: false,
  pretest_style: false,
}

export const defaultFormValues: FormValues = {
  hasOptionRationale: true,
  features: defaultFeatureValues,
}
